import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Expand } from 'lucide-react';

export function PhotoGallery({ images = [], title = '' }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);

    if (!images.length) {
        return (
            <div className="aspect-[16/10] rounded-xl bg-[var(--brand-gray-soft)] flex items-center justify-center" data-testid="vehicle-gallery">
                <p className="text-[hsl(var(--muted-foreground))] text-sm">Sin imágenes disponibles</p>
            </div>
        );
    }

    const goTo = (index) => {
        setCurrentIndex(Math.max(0, Math.min(index, images.length - 1)));
    };

    return (
        <div data-testid="vehicle-gallery" className="w-full overflow-hidden">
            {/* Main Image */}
            <div className="relative aspect-[16/10] rounded-lg sm:rounded-xl overflow-hidden bg-[var(--brand-gray-soft)] group cursor-pointer" onClick={() => setFullscreen(true)}>
                <img
                    src={images[currentIndex]}
                    alt={`${title} - Foto ${currentIndex + 1}`}
                    className="w-full h-full object-cover"
                />

                {/* Nav arrows - always visible on mobile */}
                {images.length > 1 && (
                    <>
                        <button
                            className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 hover:bg-white shadow-sm"
                            onClick={(e) => { e.stopPropagation(); goTo(currentIndex - 1); }}
                            disabled={currentIndex === 0}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 hover:bg-white shadow-sm"
                            onClick={(e) => { e.stopPropagation(); goTo(currentIndex + 1); }}
                            disabled={currentIndex === images.length - 1}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </>
                )}

                {/* Counter */}
                <div className="absolute bottom-2 right-2 flex items-center gap-2">
                    <span className="px-2 py-1 rounded-md bg-black/50 text-white text-xs font-medium">
                        {currentIndex + 1}/{images.length}
                    </span>
                </div>
            </div>

            {/* Thumbnails - horizontal scroll */}
            {images.length > 1 && (
                <div className="flex gap-1.5 sm:gap-2 mt-2 sm:mt-3 overflow-x-auto pb-1 scrollbar-hide">
                    {images.slice(0, 10).map((img, i) => (
                        <button
                            key={i}
                            className={`shrink-0 w-14 h-10 sm:w-16 sm:h-12 rounded-md sm:rounded-lg overflow-hidden border-2 transition-colors duration-200 ${
                                i === currentIndex ? 'border-[var(--brand-blue)]' : 'border-transparent hover:border-[hsl(var(--border))]'
                            }`}
                            onClick={() => goTo(i)}
                        >
                            <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </button>
                    ))}
                </div>
            )}

            {/* Fullscreen Dialog */}
            <Dialog open={fullscreen} onOpenChange={setFullscreen}>
                <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-0">
                    <div className="relative flex items-center justify-center h-[90vh]">
                        <img
                            src={images[currentIndex]}
                            alt={`${title} - Foto ${currentIndex + 1}`}
                            className="max-w-full max-h-full object-contain"
                        />
                        
                        {images.length > 1 && (
                            <>
                                <button
                                    className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 text-white"
                                    onClick={() => goTo(currentIndex - 1)}
                                    disabled={currentIndex === 0}
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <button
                                    className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 text-white"
                                    onClick={() => goTo(currentIndex + 1)}
                                    disabled={currentIndex === images.length - 1}
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                            </>
                        )}

                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur text-white text-sm">
                            {currentIndex + 1} / {images.length}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
